import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface NumberTickerProps {
  value: number;
  direction?: 'up' | 'down';
  duration?: number;
  delay?: number;
  decimalPlaces?: number;
  className?: string;
  formatOptions?: Intl.NumberFormatOptions;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

const NumberTicker = ({
  value,
  direction = 'up',
  duration = 1500,
  delay = 0,
  decimalPlaces = 0,
  className,
  formatOptions,
}: NumberTickerProps) => {
  const [displayValue, setDisplayValue] = useState(direction === 'down' ? value : 0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const animationRef = useRef<number>();
  const previousValueRef = useRef(direction === 'down' ? value : 0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateValue(direction === 'down' ? value : 0, direction === 'down' ? 0 : value);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (hasAnimated) {
      animateValue(previousValueRef.current, value);
    }
  }, [value, hasAnimated]);

  const animateValue = (from: number, to: number) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startTime = performance.now() + delay;
    const diff = to - from;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      
      if (elapsed < 0) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const currentValue = from + diff * easedProgress;
      
      setDisplayValue(currentValue);
      previousValueRef.current = currentValue;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(to);
        previousValueRef.current = to;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const formattedValue = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    ...formatOptions,
  }).format(Number(displayValue.toFixed(decimalPlaces)));

  return (
    <span
      ref={ref}
      className={cn('inline-block tabular-nums tracking-tight', className)}
    >
      {formattedValue}
    </span>
  );
};

export default NumberTicker;
